import { useMemo, useRef, useEffect, useState, type Dispatch, type SetStateAction, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { SEO } from "@/components/SEO";
import { SiteLayout } from "@/layouts/site-layout";
import { onePagersReduced as onePagerMetaList, OnePagerMeta } from "@/data/one-pagers";
import { cn } from "@/lib/utils";

const costDefaults = deriveCostRange(onePagerMetaList);

function getRandomGradient(): string {
    const hue1 = Math.floor(Math.random() * 360);
    const hue2 = (hue1 + 60 + Math.floor(Math.random() * 120)) % 360;
    const hue3 = (hue2 + 60 + Math.floor(Math.random() * 120)) % 360;
    const a = 0.25 + Math.random() * 0.25;
    const b = 0.25 + Math.random() * 0.25;
    return `linear-gradient(180deg, hsla(${hue1}, 80%, 60%, ${a}) 0%, hsla(${hue2}, 80%, 60%, ${b}) 50%, hsla(${hue3}, 80%, 60%, ${a}) 80%, hsla(0, 0%, 0%, 0.4) 100%)`;
}

function getPageBackdrop(): string {
    const hue = Math.floor(Math.random() * 360);
    const hueSecondary = (hue + 40 + Math.floor(Math.random() * 80)) % 360;
    return `linear-gradient(180deg, hsla(${hue}, 85%, 65%, 0.35) 0%, hsla(${hueSecondary}, 80%, 60%, 0.35) 80px, rgba(10, 10, 12, 0.98) 220px, rgba(10, 10, 12, 1) 100%)`;
}

const OnePagerDirectory = () => {
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
    const [selectedTools, setSelectedTools] = useState<string[]>([]);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [costRange, setCostRange] = useState<[number, number]>([costDefaults.min, costDefaults.max]);
    const [visibleTagsCount, setVisibleTagsCount] = useState<number>(20);
    const [popoverAlignOffset, setPopoverAlignOffset] = useState<number>(0);
    const [popoverWidth, setPopoverWidth] = useState<string>("100%");
    const sectionBackdrop = useMemo(() => getPageBackdrop(), []);

    useEffect(() => {
        function calculateOffset() {
            if (containerRef.current && filterButtonRef.current) {
                const containerRect = containerRef.current.getBoundingClientRect();
                const buttonRect = filterButtonRef.current.getBoundingClientRect();
                const offset = containerRect.left - buttonRect.left;
                setPopoverAlignOffset(offset);
                setPopoverWidth(`${containerRect.width}px`);
            }
        }
        const timeoutId = setTimeout(calculateOffset, 0);
        calculateOffset();
        window.addEventListener("resize", calculateOffset);
        window.addEventListener("scroll", calculateOffset, true);
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener("resize", calculateOffset);
            window.removeEventListener("scroll", calculateOffset, true);
        };
    }, []);

    const allIndustries = useMemo(() => toSortedUnique(onePagerMetaList.flatMap(item => item.industries)), []);
    const allTools = useMemo(() => toSortedUnique(onePagerMetaList.flatMap(item => item.tools)), []);
    const allTags = useMemo(() => toSortedUnique(onePagerMetaList.flatMap(item => item.tags)), []);

    const filteredOnePagers = useMemo(
        () =>
            filterOnePagers({
                items: onePagerMetaList,
                query: searchQuery,
                industries: selectedIndustries,
                tools: selectedTools,
                tags: selectedTags,
                cost: { min: costRange[0], max: costRange[1] },
            }),
        [searchQuery, selectedIndustries, selectedTools, selectedTags, costRange],
    );

    const hasActiveFilters =
        searchQuery.trim().length > 0 ||
        selectedIndustries.length > 0 ||
        selectedTools.length > 0 ||
        selectedTags.length > 0 ||
        costRange[0] !== costDefaults.min ||
        costRange[1] !== costDefaults.max;

    function handleResetFilters() {
        setSearchQuery("");
        setSelectedIndustries([]);
        setSelectedTools([]);
        setSelectedTags([]);
        setCostRange([costDefaults.min, costDefaults.max]);
        setVisibleTagsCount(20);
    }

    function handleNavigate(meta: OnePagerMeta) {
        navigate(`/one-pagers/${meta.slug}`);
    }

    return (
        <SiteLayout>
            <SEO
                title="AI One-Pagers Library | OneWeekMVPs"
                description="Explore battle-tested AI one-pager concepts with pricing, scope, tooling and delivery details. Filter by industry, tech stack, budget and more."
                canonicalUrl={`${typeof window !== "undefined" ? window.location.origin : "https://oneweekmvps.com"}/one-pagers`}
            />
            <section
                className="py-16 md:py-24"
            // style={{ backgroundImage: sectionBackdrop }}
            >
                <div ref={containerRef} className="mx-auto flex max-w-7xl flex-col gap-10 px-4 sm:px-6 lg:px-8">
                    <header className="flex flex-col gap-6">
                        <div>
                            <p className="text-sm font-medium uppercase tracking-[0.3em] text-muted-foreground">One-Pager Library</p>
                            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-foreground md:text-4xl lg:text-5xl">
                                Ship-ready AI playbooks for every team
                            </h1>
                            <p className="mt-4 max-w-3xl text-base text-muted-foreground md:text-lg">
                                Browse 19 vetted build scopes covering RAG knowledge apps, automation agents, marketing engines and more. Filter by industry, tooling
                                and budget to find the perfect starting point for your next sprint.
                            </p>
                        </div>
                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div className="flex w-full flex-1 items-center gap-3 justify-between">
                                <div className="relative flex flex-1 items-center md:max-w-xl">
                                    <Search className="pointer-events-none absolute left-4 h-5 w-5 text-muted-foreground" />
                                    <Input
                                        value={searchQuery}
                                        onChange={event => setSearchQuery(event.target.value)}
                                        placeholder="Search by title, keywords or outcomes..."
                                        className="h-12 rounded-xl bg-muted/30 pl-11 text-base focus-visible:ring-foreground"
                                        aria-label="Search one-pagers"
                                    />
                                </div>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button ref={filterButtonRef} variant="outline" className="hidden h-12 rounded-xl px-4 md:inline-flex" aria-label="Open filters">
                                            <SlidersHorizontal className="mr-2 h-4 w-4" />
                                            Filters
                                            {hasActiveFilters ? (
                                                <Badge variant="secondary" className="ml-2 h-5 min-w-5 rounded-full px-1.5 text-xs">
                                                    {selectedIndustries.length + selectedTools.length + selectedTags.length +
                                                        (costRange[0] !== costDefaults.min || costRange[1] !== costDefaults.max ? 1 : 0)}
                                                </Badge>
                                            ) : null}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        align="start"
                                        alignOffset={popoverAlignOffset}
                                        sideOffset={8}
                                        className="max-w-7xl rounded-2xl border border-0 backdrop-blur-[20px] p-4 shadow-lg"
                                        style={{
                                            backgroundColor: "rgba(24, 24, 24, 0.12)",
                                            width: popoverWidth,
                                            maxWidth: "1280px"
                                        }}
                                    >
                                        <div className="mb-4 flex items-center justify-between">
                                            <h2 className="text-lg font-semibold text-foreground">Filters</h2>
                                            <div className="flex items-center gap-3">
                                                {hasActiveFilters ? (
                                                    <Button variant="ghost" className="h-9 rounded-lg px-3 text-sm" onClick={handleResetFilters}>
                                                        <XCircle className="mr-2 h-4 w-4" />
                                                        Clear all
                                                    </Button>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                                            <div className="rounded-xl border border-border/50 bg-background/60 p-3">
                                                <h3 className="text-sm font-semibold text-foreground">Budget range</h3>
                                                <p className="mt-1.5 text-xs text-muted-foreground">Drag to narrow by estimated build budget (USD).</p>
                                                <div className="mt-3 flex flex-col gap-2">
                                                    <Slider
                                                        min={costDefaults.min}
                                                        max={costDefaults.max}
                                                        step={1000}
                                                        value={costRange}
                                                        onValueChange={value => setCostRange([value[0], value[1]])}
                                                        className="py-2"
                                                        aria-label="Budget range slider"
                                                    />
                                                    <div className="flex items-center justify-between text-sm text-foreground">
                                                        <span>${costRange[0] / 1000}K</span>
                                                        <span>${costRange[1] / 1000}K</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <FilterBlock title="Industries" description="Pick the industries you want to explore.">
                                                <div className="max-h-[200px] overflow-y-auto pr-2">
                                                    <div className="flex flex-col gap-2">
                                                        {allIndustries.map(industry => (
                                                            <label key={industry} className="flex items-start gap-2 text-sm text-foreground">
                                                                <Checkbox
                                                                    checked={selectedIndustries.includes(industry)}
                                                                    onCheckedChange={() => toggleValue(setSelectedIndustries)(industry)}
                                                                    aria-label={`Filter by ${industry}`}
                                                                />
                                                                <span>{industry}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            </FilterBlock>
                                            <FilterBlock title="Technologies" description="Filter concepts by their primary tooling.">
                                                <div className="max-h-[200px] overflow-y-auto pr-2">
                                                    <div className="flex flex-col gap-2">
                                                        {allTools.map(tool => (
                                                            <label key={tool} className="flex items-start gap-2 text-sm text-foreground">
                                                                <Checkbox
                                                                    checked={selectedTools.includes(tool)}
                                                                    onCheckedChange={() => toggleValue(setSelectedTools)(tool)}
                                                                    aria-label={`Filter by ${tool}`}
                                                                />
                                                                <span>{tool}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            </FilterBlock>
                                            <FilterBlock title="Tags" description="Highlight specific outcomes or capabilities.">
                                                <div className="max-h-[200px] overflow-y-auto pr-2">
                                                    <div className="flex flex-col gap-2">
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {allTags.slice(0, visibleTagsCount).map(tag => (
                                                                <Button
                                                                    key={tag}
                                                                    type="button"
                                                                    variant={selectedTags.includes(tag) ? "default" : "outline"}
                                                                    className={cn(
                                                                        "rounded-full px-2.5 py-1 text-xs font-medium transition",
                                                                        selectedTags.includes(tag) ? "shadow-md" : "bg-muted/40 hover:bg-muted/70",
                                                                    )}
                                                                    onClick={() => toggleValue(setSelectedTags)(tag)}
                                                                >
                                                                    {tag}
                                                                </Button>
                                                            ))}
                                                        </div>
                                                        {visibleTagsCount < allTags.length ? (
                                                            <Button
                                                                variant="ghost"
                                                                className="mt-1.5 h-7 w-full rounded-lg text-xs"
                                                                onClick={() => setVisibleTagsCount(prev => Math.min(prev + 20, allTags.length))}
                                                            >
                                                                Load more ({allTags.length - visibleTagsCount} remaining)
                                                            </Button>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </FilterBlock>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="flex items-center gap-3 md:gap-4">
                                <Sheet>
                                    <SheetTrigger asChild>
                                        <Button variant="outline" className="h-11 rounded-xl px-4 md:hidden" aria-label="Open filters">
                                            <SlidersHorizontal className="h-4 w-4" />
                                            Filters
                                        </Button>
                                    </SheetTrigger>
                                    <SheetContent side="left" className="w-full max-w-sm overflow-y-auto bg-background">
                                        <SheetHeader className="pb-6">
                                            <SheetTitle className="text-left text-xl">Filters</SheetTitle>
                                        </SheetHeader>
                                        <FilterSidebar
                                            industries={allIndustries}
                                            selectedIndustries={selectedIndustries}
                                            onToggleIndustry={toggleValue(setSelectedIndustries)}
                                            tools={allTools}
                                            selectedTools={selectedTools}
                                            onToggleTool={toggleValue(setSelectedTools)}
                                            tags={allTags}
                                            selectedTags={selectedTags}
                                            onToggleTag={toggleValue(setSelectedTags)}
                                            costRange={costRange}
                                            defaultCostRange={[costDefaults.min, costDefaults.max]}
                                            onCostChange={value => setCostRange([value[0], value[1]])}
                                        />
                                        <div className="mt-8 flex gap-3">
                                            <Button className="flex-1 rounded-xl" onClick={handleResetFilters} variant="ghost">
                                                Reset
                                            </Button>
                                            <SheetClose asChild>
                                                <Button className="flex-1 rounded-xl">Apply</Button>
                                            </SheetClose>
                                        </div>
                                    </SheetContent>
                                </Sheet>
                                {hasActiveFilters ? (
                                    <Button variant="ghost" className="hidden h-11 rounded-xl px-4 md:inline-flex" onClick={handleResetFilters}>
                                        <XCircle className="h-4 w-4" />
                                        Clear filters
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                        {selectedTags.length > 0 || selectedIndustries.length > 0 || selectedTools.length > 0 ? (
                            <div className="flex flex-wrap items-center gap-2">
                                {[...selectedTags, ...selectedIndustries, ...selectedTools].map(active => (
                                    <Badge key={active} variant="secondary" className="flex items-center gap-1 rounded-full px-3 py-1">
                                        {active}
                                        <button
                                            type="button"
                                            onClick={() => handleActiveBadgeRemove({ value: active, setSelectedIndustries, setSelectedTags, setSelectedTools })}
                                            className="ml-1 text-muted-foreground transition hover:text-foreground focus:outline-none"
                                            aria-label={`Remove ${active}`}
                                        >
                                            <XCircle className="h-3.5 w-3.5" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        ) : null}
                    </header>

                    <main className="space-y-6">
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                            <span>
                                {filteredOnePagers.length} {filteredOnePagers.length === 1 ? "one-pager" : "one-pagers"} found
                            </span>
                        </div>
                        {filteredOnePagers.length === 0 ? (
                            <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-2xl border border-border/60 bg-muted/20 p-8 text-center">
                                <p className="text-base font-semibold text-foreground">No one-pagers match your filters yet.</p>
                                <p className="max-w-md text-sm text-muted-foreground">
                                    Try widening the budget slider or clearing a few tags to see more automation concepts that fit your roadmap.
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                                {filteredOnePagers.map(meta => (
                                    <OnePagerCard key={meta.slug} meta={meta} onClick={() => handleNavigate(meta)} />
                                ))}
                            </div>
                        )}
                    </main>
                </div>
            </section>
        </SiteLayout>
    );
};

export default OnePagerDirectory;

function FilterSidebar({
    industries,
    selectedIndustries,
    onToggleIndustry,
    tools,
    selectedTools,
    onToggleTool,
    tags,
    selectedTags,
    onToggleTag,
    costRange,
    defaultCostRange,
    onCostChange,
}: FilterSidebarProps) {
    return (
        <div className="flex flex-col gap-6">
            <div className="rounded-xl border border-border/50 bg-background/60 p-4">
                <h3 className="text-sm font-semibold text-foreground">Budget range</h3>
                <p className="mt-2 text-xs text-muted-foreground">Drag to narrow by estimated build budget (USD).</p>
                <div className="mt-4 flex flex-col gap-3">
                    <Slider
                        min={defaultCostRange[0]}
                        max={defaultCostRange[1]}
                        step={1000}
                        value={costRange}
                        onValueChange={onCostChange}
                        className="py-2"
                        aria-label="Budget range slider"
                    />
                    <div className="flex items-center justify-between text-sm text-foreground">
                        <span>${costRange[0] / 1000}K</span>
                        <span>${costRange[1] / 1000}K</span>
                    </div>
                </div>
            </div>

            <FilterBlock title="Industries" description="Pick the industries you want to explore.">
                <ScrollArea className="h-40 pr-2">
                    <div className="flex flex-col gap-3">
                        {industries.map(industry => (
                            <label key={industry} className="flex items-start gap-3 text-sm text-foreground">
                                <Checkbox
                                    checked={selectedIndustries.includes(industry)}
                                    onCheckedChange={() => onToggleIndustry(industry)}
                                    aria-label={`Filter by ${industry}`}
                                />
                                <span>{industry}</span>
                            </label>
                        ))}
                    </div>
                </ScrollArea>
            </FilterBlock>

            <FilterBlock title="Technologies" description="Filter concepts by their primary tooling.">
                <ScrollArea className="h-40 pr-2">
                    <div className="flex flex-col gap-3">
                        {tools.map(tool => (
                            <label key={tool} className="flex items-start gap-3 text-sm text-foreground">
                                <Checkbox checked={selectedTools.includes(tool)} onCheckedChange={() => onToggleTool(tool)} aria-label={`Filter by ${tool}`} />
                                <span>{tool}</span>
                            </label>
                        ))}
                    </div>
                </ScrollArea>
            </FilterBlock>

            <FilterBlock title="Tags" description="Highlight specific outcomes or capabilities.">
                <div className="flex flex-wrap gap-2">
                    {tags.map(tag => (
                        <Button
                            key={tag}
                            type="button"
                            variant={selectedTags.includes(tag) ? "default" : "outline"}
                            className={cn(
                                "rounded-full px-3 py-1 text-xs font-medium transition",
                                selectedTags.includes(tag) ? "shadow-md" : "bg-muted/40 hover:bg-muted/70",
                            )}
                            onClick={() => onToggleTag(tag)}
                        >
                            {tag}
                        </Button>
                    ))}
                </div>
            </FilterBlock>
        </div>
    );
}

function FilterBlock({ title, description, children }: FilterBlockProps) {
    return (
        <div className="flex flex-col gap-2 rounded-xl border border-border/40 bg-background/70 p-3">
            <div>
                <h3 className="text-sm font-semibold text-foreground">{title}</h3>
                {description ? <p className="mt-1 text-xs text-muted-foreground">{description}</p> : null}
            </div>
            {children}
        </div>
    );
}

function filterOnePagers({ items, query, industries, tools, tags, cost }: FilterParams): OnePagerMeta[] {
    const normalisedQuery = query.trim().toLowerCase();
    return items
        .filter(item => {
            const matchesQuery =
                normalisedQuery.length === 0 ||
                item.title.toLowerCase().includes(normalisedQuery) ||
                item.description.toLowerCase().includes(normalisedQuery) ||
                item.tags.some(tag => tag.toLowerCase().includes(normalisedQuery));

            const matchesIndustry = industries.length === 0 || industries.some(industry => item.industries.includes(industry));
            const matchesTools = tools.length === 0 || tools.some(tool => item.tools.includes(tool));
            const matchesTags = tags.length === 0 || tags.every(tag => item.tags.includes(tag));
            const matchesCost = item.costRange.max >= cost.min && item.costRange.min <= cost.max;

            return matchesQuery && matchesIndustry && matchesTools && matchesTags && matchesCost;
        })
        .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
}

function toSortedUnique(values: string[]): string[] {
    return Array.from(new Set(values)).sort((left, right) => left.localeCompare(right));
}

function deriveCostRange(items: OnePagerMeta[]): { min: number; max: number } {
    const minCatalog = Math.min(...items.map(item => item.costRange.min));
    const min = Math.min(1000, minCatalog) === 1000 ? 1000 : Math.max(1000, minCatalog);
    const max = Math.max(...items.map(item => item.costRange.max));
    return { min, max };
}

function toggleValue(setter: Dispatch<SetStateAction<string[]>>) {
    return (value: string) => {
        setter(current => (current.includes(value) ? current.filter(item => item !== value) : [...current, value]));
    };
}

function handleActiveBadgeRemove({
    value,
    setSelectedIndustries,
    setSelectedTags,
    setSelectedTools,
}: HandleActiveBadgeRemoveParams) {
    setSelectedIndustries(current => (current.includes(value) ? current.filter(item => item !== value) : current));
    setSelectedTags(current => (current.includes(value) ? current.filter(item => item !== value) : current));
    setSelectedTools(current => (current.includes(value) ? current.filter(item => item !== value) : current));
}

function OnePagerCard({ meta, onClick }: { meta: OnePagerMeta; onClick: () => void }) {
    const topGradient = useMemo(() => getRandomGradient(), []);
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "group relative flex h-full flex-col rounded-2xl border border-border/60 bg-gradient-to-br p-[1px] text-left shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                meta.accentClass,
            )}
        >
            <div className="flex h-full flex-col overflow-hidden rounded-[1.05rem] bg-background/40 p-6">
                {/* <div
                    aria-hidden
                    className="-m-6 mb-4 h-20 rounded-t-[1.05rem]"
                    style={{ backgroundImage: topGradient }}
                /> */}
                <Badge variant="secondary" className="w-fit rounded-full bg-muted/50 px-3 py-1 text-xs font-medium text-muted-foreground">
                    {meta.costRange.label}
                </Badge>
                <h2 className="mt-4 text-xl font-semibold text-foreground transition-colors group-hover:text-primary">{meta.title}</h2>
                <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{meta.excerpt}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                    {meta.tags.slice(0, 3).map(tag => (
                        <Badge key={tag} variant="outline" className="rounded-full border-border/60 bg-muted/20 px-3 py-1 text-xs">
                            {tag}
                        </Badge>
                    ))}
                </div>
                <div className="mt-auto flex items-center justify-between pt-6 text-sm font-medium text-primary">
                    <span>View one-pager</span>
                    <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
                </div>
            </div>
        </button>
    );
}

interface FilterSidebarProps {
    industries: string[];
    selectedIndustries: string[];
    onToggleIndustry: (value: string) => void;
    tools: string[];
    selectedTools: string[];
    onToggleTool: (value: string) => void;
    tags: string[];
    selectedTags: string[];
    onToggleTag: (value: string) => void;
    costRange: [number, number];
    defaultCostRange: [number, number];
    onCostChange: (value: number[]) => void;
}

interface FilterBlockProps {
    title: string;
    description?: string;
    children: ReactNode;
}

interface FilterParams {
    items: OnePagerMeta[];
    query: string;
    industries: string[];
    tools: string[];
    tags: string[];
    cost: { min: number; max: number };
}

interface HandleActiveBadgeRemoveParams {
    value: string;
    setSelectedIndustries: Dispatch<SetStateAction<string[]>>;
    setSelectedTags: Dispatch<SetStateAction<string[]>>;
    setSelectedTools: Dispatch<SetStateAction<string[]>>;
}

